import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Chip, Divider } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import BaseCard from './BaseCard';
import { DESERT_FLOWER, PEANUT_BUTTER_ORANGE, COOL_GREEN, DARK_GREY } from '../../../dashboardColors';

const getBadgeColor = (pct) => {
  if (pct <= 5) return COOL_GREEN;
  if (pct <= 10) return PEANUT_BUTTER_ORANGE;
  return DESERT_FLOWER;
};

const StatusDot = ({ color }) => (
  <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
);

StatusDot.propTypes = { color: PropTypes.string.isRequired };

const MaintenanceViewCard = ({ redSensors, totalSensors, deviceTypesData, t }) => {
  const percentage = totalSensors > 0 ? (redSensors / totalSensors) * 100 : 0;
  const badgeColor = getBadgeColor(percentage);

  const deviceTypesSummary = React.useMemo(() => {
    if (!deviceTypesData?.length) return [];

    const grouped = deviceTypesData.reduce((acc, item) => {
      if (!acc[item.device_type] || item.last_update_at > acc[item.device_type].last_update_at) {
        acc[item.device_type] = item;
      }
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.device_type - b.device_type);
  }, [deviceTypesData]);

  return (
    <BaseCard title={t('maintenance_view')} icon={<BuildIcon />} className="maintenance-view-card">
      <Box display="flex" flexDirection="column" height="100%">
        <Box mb={1.5}>
          <Box display="flex" alignItems="flex-end" gap={1.5}>
            <Box display="flex" alignItems="baseline" gap={0.5}>
              <Typography variant="h5" sx={{ color: DESERT_FLOWER, fontWeight: 700, fontSize: '1.8rem' }}>
                {redSensors}
              </Typography>
              <Typography variant="body2" sx={{ color: DARK_GREY }}>/</Typography>
              <Typography variant="h5" sx={{ color: DARK_GREY, fontWeight: 700, fontSize: '1.8rem' }}>
                {totalSensors}
              </Typography>
            </Box>

            <Chip
              icon={<PriorityHighIcon sx={{ color: '#fff !important', fontSize: '1.2rem' }} />}
              label={`${percentage.toFixed(1)}%`}
              size="small"
              sx={{ fontWeight: 'bold', backgroundColor: badgeColor, color: '#fff', fontSize: '1.3rem', '& .MuiChip-icon': { color: '#fff' } }}
            />

            <Typography variant="body1" sx={{ color: DARK_GREY, fontSize: '1.3rem', fontWeight: 500, alignSelf: 'flex-end' }}>
              {t('inactive_sensors') || 'Inactive Sensors'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {deviceTypesSummary.length > 0 ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              {t('by_device_type') || 'BY DEVICE TYPE'}
            </Typography>
            {deviceTypesSummary.map((deviceType) => (
              <Box
                key={deviceType.device_type}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={0.5}
                sx={{ '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 0.5 }}
              >
                <Typography variant="body2" fontWeight="500" sx={{ flex: 1 }}>
                  {deviceType.Meaning}
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <StatusDot color={COOL_GREEN} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: '24px' }}>
                      {deviceType.num_active_sensors}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <StatusDot color={DESERT_FLOWER} />
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: '24px' }}>
                      {deviceType.num_inactive_sensors}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('no_device_type_data') || 'No device type data available'}
            </Typography>
          </Box>
        )}
      </Box>
    </BaseCard>
  );
};

MaintenanceViewCard.propTypes = {
  redSensors: PropTypes.number,
  totalSensors: PropTypes.number,
  deviceTypesData: PropTypes.array,
  t: PropTypes.func.isRequired
};

export default MaintenanceViewCard;
